import { Grid } from '@mui/material';
import { CustomFields, CustomFieldsMapper, ReportTask, ReportTaskStatus } from '@nx-smartmonkey/shared/domain';
import { CrossIcon, HSpacer, RoutalPalette } from '@nx-smartmonkey/ui';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { default as CheckIcon } from '../../../../../../../../assets/svg/CheckIcon';
import {
  DriverProjectFieldElement,
  DriverProjectFields,
} from '../../../../../../../../domain/driversProject/DriversProject';
import { StopTask } from '../../../../../../../../domain/stops/StopTask';
import { intl } from '../../../../../../../../helpers/IntGlobalProvider';
import Button from '../../../../../../../../react-components/Button';
import { Text } from '../../../../../../../../react-components/Text';
import { ReportFields } from '../../components/ReportFields';
import { TaskFooterButton } from './TaskFooterButton';

interface TaskCustomFieldsProps {
  customFields: DriverProjectFields;
  task: StopTask;
  reportTask: ReportTask;
  status: ReportTaskStatus;
  onClose: () => void;
  onConfirm: (reportTask: CustomFields) => void;
}

export const TaskCustomFields = ({
  customFields,
  task,
  reportTask,
  status,
  onClose,
  onConfirm,
}: TaskCustomFieldsProps) => {
  const [reportFields, setReportFields] = useState<CustomFields>(CustomFields.create({}));
  const [haveLeft, setHaveLeft] = useState(true);
  const [showMandatoryFields, setShowMandatoryFields] = useState(false);

  //This is only for a cleaner implementation. Comments is the only non custom field in tasks. So we add a label just for the translation
  const customFieldsEdited = useMemo(
    () =>
      customFields.map((field) => {
        if (field.id === `comments` && !field.is_custom_field) {
          return { ...field, label: `drivers.task.comments.no_custom_field` };
        }
        return field;
      }),
    [customFields]
  );

  useEffect(() => {
    const customFieldsValueObject = CustomFields.create({});
    customFieldsEdited.forEach((field) => {
      customFieldsValueObject.update({ [field.id]: undefined });
    });
    setReportFields(customFieldsValueObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFieldsEdited]);

  useEffect(() => {
    checkRequired();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportFields]);

  const checkRequired = () => {
    let haveLeft = false;
    customFieldsEdited.forEach((field) => {
      const value = reportFields.value[field.id];
      // It's important to check specifically for undefined and null, because the value can be `false` or `0`.
      if (field.is_required && (value === undefined || value === null || value === ``)) {
        haveLeft = true;
      }
    });
    setHaveLeft(haveLeft);
    return haveLeft;
  };

  useEffect(() => {
    if (reportTask.custom_fields) {
      setReportFields(CustomFields.create(CustomFieldsMapper.toDTO(reportTask.custom_fields)));
    }
  }, [reportTask]);

  function addData(fieldName: string, fieldValue: number | string | boolean | string[]) {
    if (_.isEqual(reportFields.props[fieldName], fieldValue) && typeof fieldValue !== `number`) {
      reportFields.update({ [fieldName]: undefined });
    } else {
      reportFields.update({ [fieldName]: fieldValue });
    }
    setReportFields(CustomFields.create(CustomFieldsMapper.toDTO(reportFields)));
  }

  const checkAndReturn = () => {
    if (!checkRequired()) {
      const customFieldsReturn = CustomFields.create({});
      customFieldsEdited.forEach((field) => {
        if (reportFields.value[field.id]) {
          customFieldsReturn.update({ [field.id]: reportFields.value[field.id] });
        }
      });
      onConfirm(customFieldsReturn);
    } else {
      console.error(`Still required fields left`);
    }
  };

  if (customFieldsEdited.length === 0) {
    checkAndReturn();
  }

  return (
    <Grid
      style={{
        position: `absolute`,
        inset: `0px`,
        backgroundColor: `rgb(255,255,255, 0.3)`,
        padding: `0px`,
        zIndex: 1004,
      }}
    >
      <Grid
        style={{
          width: `100%`,
          height: `100%`,
          backgroundColor: RoutalPalette.white,
          boxShadow: `rgba(149, 157, 165, 0.8) 0px 8px 24px`,
          borderRadius: `10px`,
          display: `flex`,
          flexDirection: `column`,
          paddingBottom: `75px`,
        }}
      >
        {/* Header */}
        <Grid
          style={{
            width: `100%`,
            display: `flex`,
            flexDirection: `row`,
            justifyContent: `space-between`,
            borderBottom: `1px solid ${RoutalPalette.grey4}`,
            padding: `15px 18px`,
            alignItems: `center`,
          }}
        >
          <Text style={{ color: RoutalPalette.grey1, fontSize: `20px`, fontWeight: `bold` }}>
            {task.label ? task.label : intl.formatMessage({ id: `driver.task.label.empty` })}
          </Text>
          <Button
            variant="text"
            style={{
              minWidth: `fit-content`,
              width: `fit-content`,
              backgroundColor: `transparent`,
              color: RoutalPalette.grey1,
              padding: 0,
            }}
            onClick={onClose}
          >
            <CrossIcon />
          </Button>
        </Grid>

        {/* Content */}
        <Grid style={{ padding: `20px 18px`, overflow: `auto` }}>
          {customFieldsEdited.map((field: DriverProjectFieldElement, index: number) => {
            return (
              <ReportFields
                customFields={reportFields}
                addData={addData}
                showIcon={false}
                fieldElement={field}
                position={index}
                showMandatory={showMandatoryFields}
              />
            );
          })}
        </Grid>

        {/* Footer */}
        <TaskFooterButton
          onClick={() => {
            if (haveLeft) {
              setShowMandatoryFields(true);
            } else {
              checkAndReturn();
            }
          }}
          // disabled={haveLeft}
          buttonColor={status === `canceled` ? RoutalPalette.error.medium : RoutalPalette.success.medium}
        >
          <>
            {status === `canceled` ? (
              <CrossIcon style={{ height: `18px`, width: `18px`, fill: RoutalPalette.white, marginBottom: `-4px` }} />
            ) : (
              <CheckIcon style={{ height: `20px`, width: `20px`, fill: RoutalPalette.white }} />
            )}
            <HSpacer small />
          </>

          <Text
            style={{ color: RoutalPalette.white, fontSize: `16px` }}
            intlId={status === `canceled` ? `driver.stop.report.tasks.cancel` : `driver.stop.report.tasks.complete`}
          />
        </TaskFooterButton>
      </Grid>
    </Grid>
  );
};
